import { flexRender, type Table as TanstackTable } from "@tanstack/react-table";
import type * as React from "react";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "../components/table";
import { cn } from "../lib/utils";
import { DataTablePagination } from "./data-table-pagination";
import { DataTableSkeleton } from "./data-table-skeleton";
import { getCommonPinningStyles } from "./lib";

interface DataTableProps<TData> extends React.ComponentProps<"div"> {
	table: TanstackTable<TData>;
	actionBar?: React.ReactNode;
	isLoading?: boolean;
	loadingRowCount?: number;
	loadingFilterCount?: number;
	cellWidths?: string[];
	/**
	 * Applied to every body row. Needed for row-level affordances such as a
	 * stretched link overlay, which requires the row to be a positioned
	 * ancestor.
	 */
	rowClassName?: string;
	/** Replaces the default "No results." row. */
	emptyState?: React.ReactNode;
}

export function DataTable<TData>({
	table,
	actionBar,
	children,
	className,
	isLoading = false,
	loadingRowCount = 10,
	loadingFilterCount = 0,
	cellWidths = ["auto"],
	rowClassName,
	emptyState,
	...props
}: DataTableProps<TData>) {
	// TanStack returns one table instance and mutates its state in place, so the
	// `table` prop never changes identity. React Compiler, which `apps/web`
	// enables, reads that as "nothing changed" and serves this subtree from its
	// cache, so paging, sorting and filtering updated the URL and refetched while
	// the rendered rows and the pager stayed on the first page. Every component
	// in this kit that reads through a table or column instance opts out for the
	// same reason, and a consumer that renders one has to opt out too, since the
	// element it builds is cached on the same unchanging identity. The directive
	// is inert wherever the compiler is not enabled.
	"use no memo";

	// Show skeleton when loading
	if (isLoading) {
		return (
			<div
				className={cn(
					"flex w-full min-w-0 flex-col gap-2.5 overflow-auto",
					className,
				)}
				{...props}
			>
				{/* {children} */}
				<DataTableSkeleton
					columnCount={table.getAllColumns().length}
					rowCount={loadingRowCount}
					filterCount={loadingFilterCount}
					cellWidths={cellWidths}
					withPagination
					shrinkZero
				/>
			</div>
		);
	}

	return (
		// `min-w-0` is what makes the horizontal scroll actually belong to this
		// container. Without it a flex item keeps its automatic minimum, so a
		// table wider than the viewport (nine columns of fixed `cellWidths` is
		// enough) stretches the shell's `<main>` instead, and the whole page
		// scrolls sideways, sticky header included.
		<div
			className={cn(
				"flex w-full min-w-0 flex-col gap-2.5 overflow-auto",
				className,
			)}
			{...props}
		>
			{children}
			<div className="min-w-0 overflow-x-auto rounded-md border">
				<Table>
					<TableHeader>
						{table.getHeaderGroups().map((headerGroup) => (
							<TableRow key={headerGroup.id}>
								{headerGroup.headers.map((header) => (
									<TableHead
										key={header.id}
										colSpan={header.colSpan}
										style={{
											...getCommonPinningStyles({
												column: header.column,
											}),
										}}
									>
										{header.isPlaceholder
											? null
											: flexRender(
													header.column.columnDef.header,
													header.getContext(),
												)}
									</TableHead>
								))}
							</TableRow>
						))}
					</TableHeader>
					<TableBody>
						{table.getRowModel().rows?.length ? (
							table.getRowModel().rows.map((row) => (
								<TableRow
									key={row.id}
									data-state={row.getIsSelected() && "selected"}
									className={rowClassName}
								>
									{row.getVisibleCells().map((cell) => (
										<TableCell
											key={cell.id}
											style={{
												...getCommonPinningStyles({
													column: cell.column,
												}),
											}}
										>
											{flexRender(
												cell.column.columnDef.cell,
												cell.getContext(),
											)}
										</TableCell>
									))}
								</TableRow>
							))
						) : (
							<TableRow>
								<TableCell
									colSpan={table.getAllColumns().length}
									className={emptyState ? "p-4" : "h-24 text-center"}
								>
									{emptyState ?? "No results."}
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</div>
			<div className="flex flex-col gap-2.5">
				<DataTablePagination table={table} />
				{actionBar &&
					table.getFilteredSelectedRowModel().rows.length > 0 &&
					actionBar}
			</div>
		</div>
	);
}
